import { useEffect, useState } from "react";

/**
 * useScrollRestoration
 *
 * **Why this hook?**
 * - Previously, scroll restoration logic was mixed within the component.
 * - Keeping it separate improves readability, reusability, and testability.
 * - It ensures that scroll restoration happens only **once** when returning to the page.
 *
 * **What does it do?**
 * - Retrieves the saved scroll position from `sessionStorage` and applies it **only once**.
 * - Automatically clears the saved position after restoring, so the user doesn’t keep landing in the same spot.
 * - Provides a function `saveScrollPosition()` that should be called before navigating away.
 *
 * @param dependency - A value (e.g., when products are loaded) that determines when to restore scroll.
 * @returns {Function} saveScrollPosition - Call this before navigation to remember the current scroll position.
 */

// Define a constant key for sessionStorage to prevent repetition
const SCROLL_POSITION_KEY = "useScrollRestoration:scrollPosition";

export function useScrollRestoration(dependency: any) {
  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    if (!isRestored && dependency) {
      const savedScrollY = sessionStorage.getItem(SCROLL_POSITION_KEY);

      if (savedScrollY) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: parseInt(savedScrollY, 10), behavior: "instant" });
          sessionStorage.removeItem(SCROLL_POSITION_KEY); // Clear after restoring
        });
      }

      setIsRestored(true);
    }
  }, [dependency, isRestored]);

  /**
   * Saves the current scroll position in sessionStorage.
   * This should be called before navigating away to a new page.
   */
  const saveScrollPosition = () => {
    sessionStorage.setItem(SCROLL_POSITION_KEY, window.scrollY.toString());
  };

  return saveScrollPosition;
}
